import { Injectable, inject } from '@angular/core';
import * as FHIR from 'fhirclient';
import { environment } from '../../environments/environment';
import { fhirR4 } from '@smile-cdr/fhirts';
import { fhirclient } from 'fhirclient/lib/types';
import { ResourceType } from '../models/ResourceType';
import { ParserService } from './parser.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly parserService = inject(ParserService);
  private readonly httpClient = inject(HttpClient);

  sofLaunchDone = false; // for skipping error display if SoF launch/redirect is has yet to be done
  fhirUser;
  authorOrigin;
  async launchSmartApp() {
    await FHIR.oauth2
      .authorize({
        clientId: environment.clientId,
        scope: environment.scope,
      })
      .catch((err) => {
        this.sofLaunchDone = true;
        throw err;
      });
  }

  async getClient() {
    try {
      const client = await FHIR.oauth2.ready();
      this.sofLaunchDone = true;
      return client;
    } catch (e) {
      console.log(e);
      this.sofLaunchDone = false;
      return this.launchSmartApp();
    }
  }

  async loadFhirUser() {
    const client = await this.getClient();
    this.fhirUser = client?.getFhirUser();
    if (this.fhirUser) {
      const practitionerRole = (await this.getResource(
        this.fhirUser,
      )) as fhirR4.PractitionerRole;
      this.authorOrigin = {
        extension: [
          {
            url: 'role',
            valueCodeableConcept: this.parserService.getFirstOrUndefined(
              practitionerRole.code,
            ),
          },
        ],
        url: 'author',
      };
      const practitionerString = practitionerRole?.practitioner?.reference;
      if (practitionerString) {
        const practitioner = await this.getResource(practitionerString).catch(
          () =>
            console.log('Unable to retrieve practitioner ', practitionerString),
        );
        if (practitioner)
          this.authorOrigin.extension.push({
            url: 'practitioner',
            valueReference: {
              reference: practitionerString,
              display: this.parserService.formatName(practitioner?.name),
            },
          });
      }
    }
  }

  async getResource(resource: string) {
    const client = await this.getClient();
    return client?.request(resource);
  }

  async post<T>(body, url = '/') {
    return firstValueFrom(
      this.httpClient.post<T>(url, JSON.stringify(body), {
        headers: {
          'content-type': 'application/fhir+json',
          authorization: await this.getAuth(),
        },
      }),
    );
  }

  async get<T>(url: string, params?: Record<string, string>) {
    return firstValueFrom(
      this.httpClient.get<T>(url, {
        params,
        headers: {
          authorization: await this.getAuth(),
        },
      }),
    );
  }

  async createResource(resource) {
    const client = await this.getClient();
    return client?.create(resource);
  }

  async updateResource(resource) {
    const client = await this.getClient();
    return client?.update(resource);
  }

  async deleteResource(resource) {
    const client = await this.getClient();
    return client?.delete(resource);
  }

  async getSofState() {
    const client = await this.getClient();
    return client?.getState();
  }

  async getServerUrl() {
    const client = await this.getClient();
    return client?.getState('serverUrl');
  }

  async getAuth() {
    const client = await this.getClient();
    return `Bearer ${client?.getState('tokenResponse.access_token')}`;
  }

  async createDocumentReference(file: File): Promise<fhirR4.DocumentReference> {
    const docRef = new fhirR4.DocumentReference();
    docRef.resourceType = ResourceType.DocumentReference;
    docRef.status = 'current';
    docRef.content = [{ attachment: { contentType: file.type } }];
    const resource = await this.createResource(
      docRef as fhirclient.FHIR.Resource,
    );
    return this.writeBinaryAttachment(resource, file);
  }

  async writeBinaryAttachment(
    resource: fhirclient.FHIR.Resource | undefined,
    file: File,
  ): Promise<fhirR4.DocumentReference> {
    const client = await this.getClient();
    return client?.request({
      url: `${resource?.resourceType}/${resource?.id}/$binary-access-write?path=DocumentReference.content.attachment`,
      method: 'POST',
      body: await this.convertToBase64(file),
      headers: {
        'content-type': file.type,
      },
    }) as unknown as fhirR4.DocumentReference;
  }

  convertToBase64(file: File): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Error reading file'));
    });
  }

  async logout() {
    const client = await this.getClient();
    const logoutUri =
      client?.getState('authorizeUri').split('/oauth/')[0] +
      '/logout?cb=none&revoke=token&revoke=token_refresh';
    await this.post(undefined, logoutUri);
  }
}
